import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useMyAppStore from '../../stores/myAppStore';
import { getAllowedRoutes } from '../../services/AppDataServices/config/activity-routes.config';

// Pages
import Dashboard from './pages/Dashboard';
import Activites from './pages/Activites';
import Users from './pages/Users';
import Profile from './pages/Profile';
import TestMyAppStore from './components/TestStore';
import Gestion from './pages/Gestion';
import QuickAccess from './pages/QuickAccess';

// Pages de gestion des données
import Products from './pages/DataManagement/products';
import Categories from './pages/DataManagement/products/Categories';
import Customers from './pages/DataManagement/Customers/index';
import Sales from './pages/DataManagement/Sales/index';
import Approvisionnements from './pages/DataManagement/Approvisionnements';
import Settings from './pages/DataManagement/Settings';
import ActivitySettings from './pages/DataManagement/ActivitySettings';
import ModelesVente from './pages/DataManagement/ModelesVente';
import MouvementsCaisse from './pages/DataManagement/MouvementsCaisse';
import Pos from './pages/DataManagement/pos';
import Payments from './pages/DataManagement/Payments';
import UsersManagement from './pages/DataManagement/Users';

// Pages spécifiques aux activités
import Tables from './pages/ActivitySpecific/Tables';
import Appointments from './pages/ActivitySpecific/Appointments';
import Commandes from './pages/ActivitySpecific/Commandes';

// Configuration des routes
const routesConfig = {
  '': <Navigate to="quick-access" replace />,
  'gestion': <Gestion />,
  'dashboard': <Dashboard />,
  'activites': <Activites />,
  'quick-access': <QuickAccess />,
  'users': <Users />,
  'profile': <Profile />,
  'test-store': <TestMyAppStore />,
  'products': <Products />,
  'categories': <Categories />,
  'customers': <Customers />,
  'sales': <Sales />,  
  'approvisionnements': <Approvisionnements />,
  'settings': <Settings />,
  'activity-settings': <ActivitySettings />,
  'modeles-vente': <ModelesVente />,
  'mouvements-caisse': <MouvementsCaisse />,
  'pos': <Pos />,
  'payments': <Payments />,
  'users-management': <UsersManagement />,
  'tables': <Tables />,
  'appointments': <Appointments />,
  'commandes': <Commandes />
};

// Routes toujours accessibles
const publicRoutes = ['dashboard', 'activites', 'quick-access', 'settings', 'activity-settings'];

// Hook personnalisé pour la gestion des routes
const useRouteGuard = () => {
  const { currentActivity } = useMyAppStore();
  const location = useLocation();
  
  // Extraire le chemin relatif après /myapp/
  const fullPath = location.pathname;
  const myAppIndex = fullPath.indexOf('/myapp/');
  const currentPath = myAppIndex !== -1 ? fullPath.slice(myAppIndex + 7) : '';
  
  // console.log('=== Route Guard Debug ===');
  // console.log('Full Path:', fullPath);
  // console.log('Current Path:', currentPath);
  // console.log('Current Activity:', currentActivity);

  // Mémoriser les routes autorisées
  const allowedRoutes = useMemo(() => {
    if (!currentActivity) {
      // console.log('No activity selected, using public routes:', publicRoutes);
      return publicRoutes;
    }
    const routes = [...publicRoutes, ...getAllowedRoutes(currentActivity.id)];
    // console.log('Allowed routes for activity:', routes);
    return routes;
  }, [currentActivity]);

  // Vérifier si la route actuelle est autorisée
  const isRouteAllowed = useMemo(() => {
    // Mise en cache des segments de la route actuelle
    const currentSegments = currentPath.split('/');
    
    // Vérifier si le chemin actuel correspond à une route autorisée
    return allowedRoutes.some(route => {
      // Pour les routes non-dynamiques, comparer directement
      if (!route.includes(':')) {
        return route === currentPath;
      }

      // Pour les routes dynamiques
      const routeSegments = route.split('/');
      
      // Vérifier rapidement le nombre de segments
      if (routeSegments.length !== currentSegments.length) {
        return false;
      }

      // Comparer les segments en ignorant les paramètres dynamiques
      return routeSegments.every((segment, index) => 
        segment.startsWith(':') || segment === currentSegments[index]
      );
    });
  }, [allowedRoutes, currentPath]);

  return {
    isRouteAllowed,
    currentPath
  };
};

// Composant de routage optimisé
const ProtectedRoute = ({ children }) => {
  const { isRouteAllowed } = useRouteGuard();

  if (!isRouteAllowed) {
    return <Navigate to="/404" replace />;
  }

  return children;
};

// Générer les routes avec protection
export const myAppRoutes = Object.entries(routesConfig).map(([path, element]) => ({
  path,
  element: <ProtectedRoute>{element}</ProtectedRoute>
})); 